import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SignalIcon, CheckCircleIcon, XCircleIcon, EnvelopeIcon, ArrowTopRightOnSquareIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { i18nService } from '../services/i18n';
import { getTransport } from '../transport';

interface WatcherFilter {
  enabled?: boolean;
  allowedSenders?: string[];
  blockedSenders?: string[];
  subjectPatterns?: string[];
  subjectExcludePatterns?: string[];
}

const GmailSkillConfig: React.FC = () => {
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; email?: string; error?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  const [filterEnabled, setFilterEnabled] = useState(true);
  const [allowedSenders, setAllowedSenders] = useState('');
  const [blockedSenders, setBlockedSenders] = useState('');
  const [subjectPatterns, setSubjectPatterns] = useState('');
  const [subjectExcludePatterns, setSubjectExcludePatterns] = useState('');
  const [filterSaving, setFilterSaving] = useState(false);
  const [filterMessage, setFilterMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const doLoadStatus = async () => {
    setLoading(true);
    try {
      const transport = getTransport();
      const status = transport.getGmailStatus ? await transport.getGmailStatus() : { success: false, connected: false };
      if (isMountedRef.current) {
        setConnected(!!status.connected);
        setEmail(status.email || '');
        setError(status.connected ? '' : (status.error || ''));
        if (!status.connected && !status.authUrl) {
          const authRes = transport.getGmailAuthUrl ? await transport.getGmailAuthUrl() : { success: false };
          if (authRes.success && authRes.url) {
            setAuthUrl(authRes.url);
          } else if (authRes.error) {
            setError(authRes.error);
          }
        } else if (status.authUrl) {
          setAuthUrl(status.authUrl);
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to check Gmail status');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const doLoadFilter = async () => {
    try {
      const transport = getTransport();
      if (transport.getGmailWatcherFilter) {
        const result = await transport.getGmailWatcherFilter();
        if (result.success && result.filter) {
          const f = result.filter as WatcherFilter;
          setFilterEnabled(f.enabled !== false);
          setAllowedSenders((f.allowedSenders || []).join('\n'));
          setBlockedSenders((f.blockedSenders || []).join('\n'));
          setSubjectPatterns((f.subjectPatterns || []).join('\n'));
          setSubjectExcludePatterns((f.subjectExcludePatterns || []).join('\n'));
        }
      }
    } catch {
      // Filter not available, use defaults
    }
  };

  useEffect(() => {
    doLoadStatus();
    doLoadFilter();
    return () => { isMountedRef.current = false; };
  }, []);

  const handleConnect = useCallback(() => {
    if (authUrl) {
      window.open(authUrl, '_blank', 'width=600,height=700');
    }
  }, [authUrl]);

  const handleDisconnect = useCallback(async () => {
    try {
      const transport = getTransport();
      if (transport.disconnectGmail) {
        const result = await transport.disconnectGmail();
        if (result.success) {
          setConnected(false);
          setEmail('');
          setError('');
          setAuthUrl('');
          setTestResult(null);
          await doLoadStatus();
        } else {
          setError(result.error || 'Failed to disconnect');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect');
    }
  }, []);

  const handleTest = useCallback(async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const transport = getTransport();
      const result = transport.testGmailConnection ? await transport.testGmailConnection() : { success: false, error: 'Not supported' };
      if (isMountedRef.current) {
        setTestResult(result);
        setConnected(result.success);
        if (result.email) setEmail(result.email);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setTestResult({ success: false, error: err instanceof Error ? err.message : 'Test failed' });
      }
    } finally {
      if (isMountedRef.current) {
        setIsTesting(false);
      }
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await doLoadStatus();
  }, []);

  const handleSaveFilter = useCallback(async () => {
    setFilterSaving(true);
    setFilterMessage(null);
    try {
      const transport = getTransport();
      if (transport.setGmailWatcherFilter) {
        const filter: Record<string, unknown> = {};
        filter.enabled = filterEnabled;
        filter.allowedSenders = allowedSenders.split('\n').map(s => s.trim()).filter(Boolean);
        filter.blockedSenders = blockedSenders.split('\n').map(s => s.trim()).filter(Boolean);
        filter.subjectPatterns = subjectPatterns.split('\n').map(s => s.trim()).filter(Boolean);
        filter.subjectExcludePatterns = subjectExcludePatterns.split('\n').map(s => s.trim()).filter(Boolean);
        const result = await transport.setGmailWatcherFilter(filter);
        if (result.success) {
          setFilterMessage({ type: 'success', text: 'Settings saved' });
        } else {
          setFilterMessage({ type: 'error', text: result.error || 'Failed to save' });
        }
      }
    } catch (err) {
      setFilterMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save' });
    } finally {
      setFilterSaving(false);
    }
  }, [filterEnabled, allowedSenders, blockedSenders, subjectPatterns, subjectExcludePatterns]);

  const labelClassName = 'block text-xs font-medium dark:text-claude-darkText text-claude-text mb-1';

  const textareaClassName = 'w-full rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white px-3 py-2 text-xs dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-1 focus:ring-claude-accent resize-y min-h-[60px]';

  if (loading) {
    return (
      <div className="p-4 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
        {i18nService.t('loading')}...
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface/30 bg-claude-surface/30">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium dark:text-claude-darkText text-claude-text flex items-center gap-2">
          <EnvelopeIcon className="h-4 w-4" />
          {i18nService.t('emailConfig')}
        </h4>
        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
          connected
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          {connected ? (
            <>
              <CheckCircleIcon className="h-3 w-3" />
              {i18nService.t('connectionSuccess')}
            </>
          ) : (
            <>
              <XCircleIcon className="h-3 w-3" />
              {i18nService.t('connectionFailed')}
            </>
          )}
        </span>
      </div>

      {connected && email && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-3 py-2">
          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
          <div className="text-xs dark:text-claude-darkText text-claude-text">
            Connected as <span className="font-medium">{email}</span>
          </div>
        </div>
      )}

      {error && !connected && (
        <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="space-y-3 pt-1">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary hover:text-claude-accent transition-colors"
          >
            Refresh
          </button>
        </div>

        {connected ? (
          <div className="space-y-3">
            <div>
              <label className={labelClassName}>Bridge Account</label>
              <div className="block w-full rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 px-3 py-2 text-xs dark:text-claude-darkText text-claude-text">
                {email || 'Connected'}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleTest}
                disabled={isTesting}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
              >
                <SignalIcon className="h-3.5 w-3.5 mr-1.5" />
                {isTesting ? (i18nService.t('imConnectivityTesting') || 'Testing...') : (i18nService.t('imConnectivityTest') || 'Test Connection')}
              </button>

              <button
                type="button"
                onClick={handleDisconnect}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors active:scale-[0.98]"
              >
                Disconnect
              </button>
            </div>

            {testResult && (
              <div className={`flex items-center gap-1 text-xs ${testResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {testResult.success ? (
                  <CheckCircleIcon className="h-4 w-4" />
                ) : (
                  <XCircleIcon className="h-4 w-4" />
                )}
                <span>
                  {testResult.success
                    ? `Connected as ${testResult.email || email}`
                    : testResult.error || 'Connection failed'}
                </span>
              </div>
            )}

            {/* Watcher Filter Settings */}
            <div className="border-t dark:border-claude-darkBorder border-claude-border pt-3 mt-3">
              <h5 className="text-xs font-medium dark:text-claude-darkText text-claude-text flex items-center gap-1.5 mb-3">
                <FunnelIcon className="h-3.5 w-3.5" />
                Email Watcher Filter
              </h5>
              <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mb-3">
                Control which incoming emails are forwarded to the email assistant. Emails that don&apos;t match the filter are silently skipped.
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="filterEnabled"
                    checked={filterEnabled}
                    onChange={(e) => setFilterEnabled(e.target.checked)}
                    className="rounded border-claude-border dark:border-claude-darkBorder text-claude-accent focus:ring-claude-accent h-3.5 w-3.5"
                  />
                  <label htmlFor="filterEnabled" className="text-xs dark:text-claude-darkText text-claude-text">
                    Enable email forwarding
                  </label>
                </div>

                <div>
                  <label className={labelClassName}>
                    Allowed Senders
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary font-normal ml-1">(one per line, e.g. @company.com or user@example.com)</span>
                  </label>
                  <textarea
                    value={allowedSenders}
                    onChange={(e) => setAllowedSenders(e.target.value)}
                    placeholder={'@company.com\nimportant@example.com'}
                    rows={2}
                    className={textareaClassName}
                  />
                  <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-1">
                    If set, only emails from these senders/domains are forwarded. Leave empty to allow all.
                  </p>
                </div>

                <div>
                  <label className={labelClassName}>
                    Blocked Senders
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary font-normal ml-1">(one per line)</span>
                  </label>
                  <textarea
                    value={blockedSenders}
                    onChange={(e) => setBlockedSenders(e.target.value)}
                    placeholder={'noreply@spam.com\nnewsletter@'}
                    rows={2}
                    className={textareaClassName}
                  />
                  <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-1">
                    Emails from these senders are always skipped, even if they match allowed senders.
                  </p>
                </div>

                <div>
                  <label className={labelClassName}>
                    Subject Include Patterns
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary font-normal ml-1">(wildcards: health*, alert*, *urgent*)</span>
                  </label>
                  <textarea
                    value={subjectPatterns}
                    onChange={(e) => setSubjectPatterns(e.target.value)}
                    placeholder={'health*\nalert*\n*urgent*'}
                    rows={2}
                    className={textareaClassName}
                  />
                  <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-1">
                    If set, only emails matching at least one pattern are forwarded. Leave empty to allow all subjects.
                  </p>
                </div>

                <div>
                  <label className={labelClassName}>
                    Subject Exclude Patterns
                    <span className="dark:text-claude-darkTextSecondary text-claude-textSecondary font-normal ml-1">(wildcards: newsletter*, *unsubscribe*)</span>
                  </label>
                  <textarea
                    value={subjectExcludePatterns}
                    onChange={(e) => setSubjectExcludePatterns(e.target.value)}
                    placeholder={'newsletter*\n*unsubscribe*\n*promo*'}
                    rows={2}
                    className={textareaClassName}
                  />
                  <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary mt-1">
                    Emails matching any pattern are always skipped.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveFilter}
                    disabled={filterSaving}
                    className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
                  >
                    {filterSaving ? 'Saving...' : 'Save Filter'}
                  </button>

                  {filterMessage && (
                    <span className={`text-xs ${filterMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {filterMessage.text}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
              Connect a Gmail account to enable email functionality. You&apos;ll be redirected to Google to authorize access.
            </div>

            {authUrl ? (
              <>
                <button
                  type="button"
                  onClick={handleConnect}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors active:scale-[0.98]"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                  Connect Gmail
                </button>
                <div className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  After authorizing, you&apos;ll be redirected back. Then click &quot;Refresh&quot; to verify the connection.
                </div>
              </>
            ) : (
              <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                GMAIL_CLIENT_ID and GMAIL_CLIENT_SECRET must be configured by an administrator, and GMAIL_REDIRECT_URI must be set to your server URL + /api/gmail/callback.
              </div>
            )}

            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-xl border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors active:scale-[0.98]"
            >
              Check Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GmailSkillConfig;